import { useCallback, useEffect, useState, useRef } from 'react'
import { API } from '../../api'
import { TADMembership } from '../../type'
import { EntitiesTypes } from '../../view/admin/ad-management/EntitiesTabs'
import { handleArrayBufferBackendError } from '../../utility/array-buffer-toast-handling'

export const LAST_MEMBERSHIPS_TAB_ACCESSED = 'lastMembershipsTabAccessed'

type TUseEntities = {
  isLoading: boolean
  entities: TADMembership[]
  search: string
  setSearch: (search: string) => void
  page: number
  setPage: (page: number) => void
  totalCount: number
  maxPageSize: number
  totalPages: number
  getEntities: () => void
  setEntities: (entity: TADMembership[]) => void
  handleExportEntitiesButtonClick: () => void
}

export const useEntities = (type: EntitiesTypes): TUseEntities => {
  const [isLoading, setIsLoading] = useState(true)
  const [entities, setEntities] = useState<TADMembership[]>([])
  const [search, setSearch] = useState('')
  const [entitiesPage, setEntitiesPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [maxPageSize, setMaxPageSize] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)
  const getUrl = () => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return 'pharmacies/'
      case EntitiesTypes.distributors:
        return 'distributors/'
      case EntitiesTypes.referral_types:
        return 'referral_types/'
      case EntitiesTypes.omit_measures:
        return 'omissions/'
      case EntitiesTypes.reports:
        return 'qlik_based_reports/'
      default:
        return ''
    }
  }

  const checkEntityParam = () => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return ''
      case EntitiesTypes.distributors:
        return ''
      case EntitiesTypes.referral_types:
        return ''
      case EntitiesTypes.omit_measures:
        return ''
      case EntitiesTypes.reports:
        return ''
      default:
        return ''
    }
  }

  const getMemberships = useCallback(
    async (pageToFetch: number) => {
      if (!type) {
        return
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      setIsLoading(true)
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current

      try {
        const { max_page_size, total_pages, total_count, page_number, results } = await API.ADManagement.entities.list(
          search,
          pageToFetch,
          getUrl(),
          checkEntityParam(),
          abortControllerRef.current.signal,
        )

        setMaxPageSize(Number(max_page_size))
        setTotalCount(Number(total_count))
        setTotalPages(Number(total_pages))
        setEntities(results)
        setEntitiesPage(Number(page_number))
      } catch (error) {
        setEntities([])
        setEntitiesPage(1)
        if ((error as { name?: string }).name === 'AbortError') {
          console.log('Request was aborted')
        } else {
          console.error('Request failed', error)
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    },
    [search, type],
  )

  useEffect(() => {
    setIsLoading(true)
    getMemberships(entitiesPage)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [getMemberships, entitiesPage])

  const handlePageChange = useCallback((newPage: number) => {
    setEntitiesPage(newPage)
  }, [])

  const getExportUrl = (type: EntitiesTypes): string => {
    switch (type) {
      case EntitiesTypes.pharmacies:
        return '/jarvis_api/groups/pharmacies/export_to_excel/'
      case EntitiesTypes.distributors:
        return '/jarvis_api/groups/distributors/export_to_excel/'
      case EntitiesTypes.omit_measures:
        return '/jarvis_api/groups/omissions/export_to_excel/'
      case EntitiesTypes.referral_types:
        return '/jarvis_api/groups/referral_types/export_to_excel/'
      case EntitiesTypes.reports:
        return '/jarvis_api/groups/qlik_based_reports/export_to_excel/'
      default:
        return ''
    }
  }

  const handleExportEntitiesButtonClick = () => {
    const url = getExportUrl(type)
    if (url) {
      try {
        void API.ADManagement.entities.download(url, search)
      } catch (error) {
        handleArrayBufferBackendError(error)
      }
    }
  }

  return {
    isLoading,
    entities,
    search,
    setSearch,
    page: entitiesPage,
    setPage: handlePageChange,
    totalCount,
    maxPageSize,
    totalPages,
    getEntities: () => getMemberships(entitiesPage),
    setEntities,
    handleExportEntitiesButtonClick,
  }
}
